<script lang="ts">
  /**
   * POI/map-pins admin-CRUD-vy (S-13-01b).
   *
   * Tre tillstånd:
   *  1. **list** (default): tabell över alla POI:er med filter.
   *  2. **create**: form för ny POI.
   *  3. **edit**: pre-fyllt form.
   *
   * Initialt egen tabb i Lyktan-shellen — konsolideras in i Rita-tabben
   * via S-04-17. Position-input är manuellt latitud/longitud i denna
   * iteration (kart-klick kommer i Rita-tabben).
   *
   * Hard-rules (CLAUDE.md + avvikelse 7):
   *  - PII-detektor server-side (titel + body). Klient-side validering
   *    visar felkod-mappning men servern är källa av sanning.
   *  - Soft-delete via DELETE-endpointet — aldrig hård-DELETE.
   *  - Audit-trail skrivs av backend per mutation.
   *  - Body-textarea har 200-cap counter (server-side hard cap).
   *  - Inga råa databas-ID:n exponeras (UUID:s som strängar).
   */
  import { onMount } from 'svelte';
  import {
    createPoi,
    deletePoi,
    isActive,
    listPois,
    PIN_KINDS,
    PIN_TYPES,
    pinKindLabel,
    pinTypeLabel,
    restorePoi,
    updatePoi,
    type CreatePoiInput,
    type PinKind,
    type PinType,
    type PoiRecord,
    type UpdatePoiInput,
  } from './poisClient';

  type Props = {
    onUnauthorized?: () => void;
    /**
     * Signal-counter från RitaView (UX-fix 2026-05-05): `+` och Refresh
     * är lyfta till Rita-layer-toolbar-raden. Vid klick ökas respektive
     * counter, och vi lyssnar via $effect och kallar `startCreate` /
     * `loadRecords`. Initialvärde 0 ska INTE auto-trigga create —
     * lastSeen-räknarna nedan startar på samma värde och hoppar över
     * matching.
     */
    createTrigger?: number;
    refreshTrigger?: number;
  };
  const { onUnauthorized, createTrigger = 0, refreshTrigger = 0 }: Props = $props();

  // lastSeen-räknare. Initialiseras vid första effect-körning så vi inte
  // auto-triggar create/refresh på initial-render. Måste sättas i $effect
  // för att Svelte 5 ska se prop-värdet (state_referenced_locally).
  let lastCreateTrigger: number | undefined = undefined;
  let lastRefreshTrigger: number | undefined = undefined;

  $effect(() => {
    if (lastCreateTrigger === undefined) {
      lastCreateTrigger = createTrigger;
      return;
    }
    if (createTrigger !== lastCreateTrigger) {
      lastCreateTrigger = createTrigger;
      startCreate();
    }
  });

  $effect(() => {
    if (lastRefreshTrigger === undefined) {
      lastRefreshTrigger = refreshTrigger;
      return;
    }
    if (refreshTrigger !== lastRefreshTrigger) {
      lastRefreshTrigger = refreshTrigger;
      void loadRecords();
    }
  });

  type Mode = 'list' | 'create' | 'edit';

  let mode: Mode = $state('list');
  let records: PoiRecord[] = $state([]);
  let isLoading = $state(true);
  let listErrorMessage: string | null = $state(null);

  // Filter
  let filterKind: PinKind | 'all' = $state('all');
  let filterPinType: PinType | 'all' = $state('all');
  let showDeleted = $state(false);

  // Form state
  let selectedRecord: PoiRecord | null = $state(null);
  let formPinType: PinType = $state('defibrillator');
  let formKind: PinKind = $state('info');
  let formLat: string = $state('57.738');
  let formLng: string = $state('11.948');
  let formTitle: string = $state('');
  let formBody: string = $state('');
  let formActiveFrom: string = $state('');
  let formActiveTo: string = $state('');
  let formError: string | null = $state(null);
  let isSaving = $state(false);

  let rowError: string | null = $state(null);
  let pendingActionId: string | null = $state(null);

  const BODY_MAX = 200;

  async function loadRecords(): Promise<void> {
    isLoading = true;
    listErrorMessage = null;
    const result = await listPois();
    if (result.kind === 'ok') {
      records = result.items;
    } else if (result.kind === 'unauthorized') {
      records = [];
      listErrorMessage = 'Sessionen har gått ut. Logga in igen.';
      onUnauthorized?.();
    } else if (result.kind === 'forbidden') {
      records = [];
      listErrorMessage = 'Saknar behörighet att se POI:er.';
    } else {
      records = [];
      listErrorMessage = result.message;
    }
    isLoading = false;
  }

  onMount(() => {
    void loadRecords();
  });

  let visibleRecords = $derived(
    records.filter((r) => {
      if (!showDeleted && r.deletedAt) return false;
      if (filterKind !== 'all' && r.kind !== filterKind) return false;
      if (filterPinType !== 'all' && r.pinType !== filterPinType) return false;
      return true;
    }),
  );

  function startCreate(): void {
    mode = 'create';
    selectedRecord = null;
    formPinType = 'defibrillator';
    formKind = 'info';
    formLat = '57.738';
    formLng = '11.948';
    formTitle = '';
    formBody = '';
    formActiveFrom = '';
    formActiveTo = '';
    formError = null;
  }

  function startEdit(record: PoiRecord): void {
    mode = 'edit';
    selectedRecord = record;
    formPinType = record.pinType;
    formKind = record.kind;
    formLat = String(record.latitude);
    formLng = String(record.longitude);
    formTitle = record.title;
    formBody = record.body ?? '';
    formActiveFrom = isoToLocalInput(record.activeFrom);
    formActiveTo = record.activeTo ? isoToLocalInput(record.activeTo) : '';
    formError = null;
  }

  function cancelEditor(): void {
    mode = 'list';
    selectedRecord = null;
    formError = null;
  }

  function isoToLocalInput(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number): string => n.toString().padStart(2, '0');
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
  }

  function localInputToIso(value: string): string | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
  }

  function validateForm(): string | null {
    if (formTitle.trim().length === 0) return 'Ange en titel.';
    if (formTitle.length > 120) return 'Titel max 120 tecken.';
    if (formBody.length > BODY_MAX) return `Beskrivning max ${BODY_MAX} tecken.`;
    const lat = Number(formLat);
    const lng = Number(formLng);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) return 'Ogiltig latitud.';
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) return 'Ogiltig longitud.';
    if (formKind === 'notice' && !formActiveTo) {
      return 'Notiser kräver ett slut-datum.';
    }
    if (formActiveFrom && formActiveTo) {
      const from = new Date(formActiveFrom);
      const to = new Date(formActiveTo);
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
        return 'Ogiltigt tidsfönster.';
      }
      if (to.getTime() <= from.getTime()) {
        return 'Slut måste vara efter start.';
      }
    }
    return null;
  }

  async function handleSubmit(): Promise<void> {
    formError = null;
    const validation = validateForm();
    if (validation) {
      formError = validation;
      return;
    }
    isSaving = true;
    try {
      const lat = Number(formLat);
      const lng = Number(formLng);
      const trimmedBody = formBody.trim();
      const activeFromIso = localInputToIso(formActiveFrom);
      const activeToIso = localInputToIso(formActiveTo);

      if (mode === 'create') {
        const input: CreatePoiInput = {
          pinType: formPinType,
          kind: formKind,
          latitude: lat,
          longitude: lng,
          title: formTitle.trim(),
          body: trimmedBody.length > 0 ? trimmedBody : null,
          activeFrom: activeFromIso,
          activeTo: activeToIso,
        };
        const result = await createPoi(input);
        if (result.kind === 'ok') {
          mode = 'list';
          await loadRecords();
        } else if (result.kind === 'unauthorized') {
          onUnauthorized?.();
        } else if (result.kind === 'invalid') {
          formError = result.message;
        } else {
          formError =
            result.kind === 'forbidden'
              ? 'Saknar behörighet.'
              : result.kind === 'error'
                ? result.message
                : 'Något gick fel.';
        }
      } else if (mode === 'edit' && selectedRecord) {
        if (!activeFromIso) {
          formError = 'Ogiltigt start-datum.';
          return;
        }
        const input: UpdatePoiInput = {
          pinType: formPinType,
          kind: formKind,
          latitude: lat,
          longitude: lng,
          title: formTitle.trim(),
          body: trimmedBody.length > 0 ? trimmedBody : null,
          activeFrom: activeFromIso,
          activeTo: activeToIso,
        };
        const result = await updatePoi(selectedRecord.id, input);
        if (result.kind === 'ok') {
          mode = 'list';
          selectedRecord = null;
          await loadRecords();
        } else if (result.kind === 'unauthorized') {
          onUnauthorized?.();
        } else if (result.kind === 'invalid') {
          formError = result.message;
        } else if (result.kind === 'not_found') {
          formError = 'Pin-en finns inte längre.';
        } else {
          formError =
            result.kind === 'forbidden'
              ? 'Saknar behörighet.'
              : result.kind === 'error'
                ? result.message
                : 'Något gick fel.';
        }
      }
    } finally {
      isSaving = false;
    }
  }

  async function handleDelete(record: PoiRecord): Promise<void> {
    rowError = null;
    pendingActionId = record.id;
    try {
      const result = await deletePoi(record.id);
      if (result.kind === 'ok') {
        await loadRecords();
      } else if (result.kind === 'unauthorized') {
        onUnauthorized?.();
      } else if (result.kind === 'not_found') {
        rowError = 'Pin-en finns inte längre.';
        await loadRecords();
      } else {
        rowError =
          result.kind === 'forbidden'
            ? 'Saknar behörighet.'
            : (result as { message: string }).message;
      }
    } finally {
      pendingActionId = null;
    }
  }

  async function handleRestore(record: PoiRecord): Promise<void> {
    rowError = null;
    pendingActionId = record.id;
    try {
      const result = await restorePoi(record.id);
      if (result.kind === 'ok') {
        await loadRecords();
      } else if (result.kind === 'unauthorized') {
        onUnauthorized?.();
      } else if (result.kind === 'not_found') {
        rowError = 'Pin-en finns inte längre.';
        await loadRecords();
      } else {
        rowError =
          result.kind === 'forbidden'
            ? 'Saknar behörighet.'
            : (result as { message: string }).message;
      }
    } finally {
      pendingActionId = null;
    }
  }
</script>

<section class="pois" aria-label="POI">
  {#if mode === 'list'}
    <div class="filters" role="group" aria-label="Filter">
      <label>
        <span>Variant</span>
        <select bind:value={filterKind}>
          <option value="all">Alla</option>
          {#each PIN_KINDS as k (k)}
            <option value={k}>{pinKindLabel(k)}</option>
          {/each}
        </select>
      </label>
      <label>
        <span>Typ</span>
        <select bind:value={filterPinType}>
          <option value="all">Alla</option>
          {#each PIN_TYPES as t (t)}
            <option value={t}>{pinTypeLabel(t)}</option>
          {/each}
        </select>
      </label>
      <label class="checkbox-label">
        <input type="checkbox" bind:checked={showDeleted} />
        <span>Visa raderade</span>
      </label>
    </div>

    {#if listErrorMessage}
      <div class="alert error" role="alert">{listErrorMessage}</div>
    {/if}
    {#if rowError}
      <div class="alert error" role="alert">{rowError}</div>
    {/if}

    {#if !isLoading && visibleRecords.length === 0 && !listErrorMessage}
      <div class="empty">
        <p class="kicker">Inga POI:er än.</p>
        <p>Klicka på <strong>Skapa POI</strong> för att lägga den första markören.</p>
      </div>
    {:else if visibleRecords.length > 0}
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Titel</th>
              <th scope="col">Typ</th>
              <th scope="col">Variant</th>
              <th scope="col">Position</th>
              <th scope="col">Status</th>
              <th scope="col">Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {#each visibleRecords as record (record.id)}
              {@const active = isActive(record)}
              <tr class:inactive={!active}>
                <td>
                  <strong>{record.title}</strong>
                  {#if record.body}<p class="dim">{record.body}</p>{/if}
                </td>
                <td>{pinTypeLabel(record.pinType)}</td>
                <td>{pinKindLabel(record.kind)}</td>
                <td class="coords">{record.latitude.toFixed(4)}, {record.longitude.toFixed(4)}</td>
                <td>
                  {#if record.deletedAt}
                    <span class="badge danger">Raderad</span>
                  {:else if active}
                    <span class="badge active">Aktiv</span>
                  {:else}
                    <span class="badge dim">Inaktiv</span>
                  {/if}
                </td>
                <td class="actions">
                  {#if record.deletedAt}
                    <button
                      type="button"
                      class="btn small secondary"
                      onclick={() => handleRestore(record)}
                      disabled={pendingActionId === record.id}
                    >
                      Återställ
                    </button>
                  {:else}
                    <button
                      type="button"
                      class="btn small secondary"
                      onclick={() => startEdit(record)}
                      disabled={pendingActionId === record.id}
                    >
                      Redigera
                    </button>
                    <button
                      type="button"
                      class="btn small danger"
                      onclick={() => handleDelete(record)}
                      disabled={pendingActionId === record.id}
                    >
                      Radera
                    </button>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  {/if}

  {#if mode === 'create' || mode === 'edit'}
    <div class="editor">
      <div class="editor-header">
        <p class="eyebrow accent">{mode === 'create' ? 'Ny POI' : 'Redigera POI'}</p>
        <h3>{mode === 'create' ? 'Lägg till en markör' : (selectedRecord?.title ?? 'POI')}</h3>
        <p class="dim">
          PII-skydd: server avvisar input som innehåller personnummer, telefonnummer eller
          e-postadresser. Skriv inga sådana uppgifter.
        </p>
      </div>

      <form
        class="form"
        onsubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <div class="field-row">
          <label class="field">
            <span>Pin-typ</span>
            <select bind:value={formPinType} disabled={isSaving}>
              {#each PIN_TYPES as t (t)}
                <option value={t}>{pinTypeLabel(t)}</option>
              {/each}
            </select>
          </label>
          <label class="field">
            <span>Variant</span>
            <select bind:value={formKind} disabled={isSaving}>
              {#each PIN_KINDS as k (k)}
                <option value={k}>{pinKindLabel(k)}</option>
              {/each}
            </select>
          </label>
        </div>

        <div class="field-row">
          <label class="field">
            <span>Latitud</span>
            <input
              type="number"
              step="0.0001"
              min="-90"
              max="90"
              bind:value={formLat}
              required
              disabled={isSaving}
            />
          </label>
          <label class="field">
            <span>Longitud</span>
            <input
              type="number"
              step="0.0001"
              min="-180"
              max="180"
              bind:value={formLng}
              required
              disabled={isSaving}
            />
          </label>
        </div>

        <label class="field">
          <span>Titel</span>
          <input type="text" maxlength="120" bind:value={formTitle} required disabled={isSaving} />
        </label>

        <label class="field">
          <span>Beskrivning ({formBody.length}/{BODY_MAX} tecken)</span>
          <textarea maxlength={BODY_MAX} rows="3" bind:value={formBody} disabled={isSaving}
          ></textarea>
        </label>

        <div class="field-row">
          <label class="field">
            <span>Aktiv från (valfri)</span>
            <input type="datetime-local" bind:value={formActiveFrom} disabled={isSaving} />
          </label>
          <label class="field">
            <span>
              Aktiv till
              {#if formKind === 'notice'}
                (krävs för notis){/if}
            </span>
            <input
              type="datetime-local"
              bind:value={formActiveTo}
              disabled={isSaving}
              required={formKind === 'notice'}
            />
          </label>
        </div>

        {#if formError}
          <div class="alert error" role="alert">{formError}</div>
        {/if}

        <div class="form-actions">
          <button type="submit" class="btn primary" disabled={isSaving}>
            {isSaving ? 'Sparar…' : mode === 'create' ? 'Skapa POI' : 'Spara ändringar'}
          </button>
          <button type="button" class="btn secondary" onclick={cancelEditor} disabled={isSaving}>
            Avbryt
          </button>
        </div>
      </form>
    </div>
  {/if}
</section>

<style>
  .pois {
    display: grid;
    gap: 1rem;
  }
  header {
    display: grid;
    gap: 0.4rem;
  }
  .eyebrow {
    font-size: 0.85rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--accent);
    font-weight: 600;
    margin: 0;
  }
  .eyebrow.accent {
    color: var(--glow);
  }
  h2,
  h3 {
    margin: 0;
    color: var(--text);
  }
  h2 {
    font-size: 1.4rem;
  }
  h3 {
    font-size: 1.15rem;
  }
  .lead {
    margin: 0;
    color: var(--text-dim);
    line-height: 1.55;
    max-width: 60ch;
  }
  .dim {
    color: var(--text-dim);
    font-size: 0.85rem;
    margin: 0.15rem 0 0;
  }
  .filters {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--rule);
  }
  .filters label {
    display: flex;
    flex-direction: column;
    font-size: 0.8rem;
    color: var(--text-dim);
    gap: 0.2rem;
  }
  .filters .checkbox-label {
    flex-direction: row;
    align-items: center;
    gap: 0.4rem;
  }
  .filters select {
    background: var(--bg);
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text);
    padding: 0.35rem 0.5rem;
    font-family: inherit;
    font-size: 0.9rem;
  }
  .table-wrap {
    overflow-x: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  th,
  td {
    padding: 0.55rem 0.65rem;
    text-align: left;
    border-bottom: 1px solid var(--rule);
    vertical-align: top;
  }
  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--text-dim);
    font-weight: 600;
  }
  tr.inactive {
    opacity: 0.6;
  }
  .coords {
    font-family:
      ui-monospace,
      SFMono-Regular,
      Consolas,
      Liberation Mono,
      monospace;
    font-size: 0.85rem;
  }
  .badge {
    display: inline-block;
    padding: 0.15rem 0.5rem;
    font-size: 0.75rem;
    border-radius: 999px;
    border: 1px solid var(--rule);
    color: var(--text-dim);
  }
  .badge.active {
    background: var(--accent-soft);
    border-color: var(--accent);
    color: var(--glow);
  }
  .badge.dim {
    color: var(--text-dim);
  }
  .badge.danger {
    background: var(--danger-soft);
    border-color: var(--danger);
    color: var(--text);
  }
  .actions {
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
  }
  .btn {
    background: transparent;
    border: 1px solid var(--rule);
    color: var(--text);
    padding: 0.45rem 0.95rem;
    border-radius: 4px;
    font-family: inherit;
    font-size: 0.85rem;
    cursor: pointer;
  }
  .btn.primary {
    background: var(--accent);
    border-color: var(--accent);
    color: var(--bg);
    font-weight: 600;
  }
  .btn.primary:hover:not(:disabled) {
    background: var(--glow);
    border-color: var(--glow);
  }
  .btn.secondary:hover:not(:disabled) {
    color: var(--glow);
    border-color: var(--text-dim);
  }
  .btn.small {
    padding: 0.3rem 0.65rem;
    font-size: 0.8rem;
  }
  .btn.danger {
    background: var(--danger-soft);
    border-color: var(--danger);
    color: var(--text);
  }
  .btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  /* icon-btn-styles flyttade till RitaView (UX-fix 2026-05-05) — `+` och
     Refresh äger nu Rita-layer-toolbar-raden, inte enskilda sub-vyer. */
  .empty {
    padding: 1.5rem;
    border: 1px dashed var(--rule);
    border-radius: 6px;
    text-align: center;
    color: var(--text-dim);
  }
  .kicker {
    font-weight: 600;
    color: var(--text);
    margin: 0 0 0.25rem;
  }
  .editor {
    display: grid;
    gap: 1rem;
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 1.5rem;
    max-width: 720px;
  }
  .editor-header {
    display: grid;
    gap: 0.3rem;
  }
  .form {
    display: grid;
    gap: 0.85rem;
  }
  .field {
    display: grid;
    gap: 0.25rem;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  .field input,
  .field textarea,
  .field select {
    background: var(--bg);
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text);
    font-family: inherit;
    font-size: 0.95rem;
    padding: 0.45rem 0.6rem;
  }
  .field textarea {
    font-family: inherit;
    resize: vertical;
  }
  .field input:focus,
  .field textarea:focus,
  .field select:focus {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  .field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.85rem;
  }
  .form-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .alert {
    padding: 0.55rem 0.8rem;
    border-radius: 4px;
    font-size: 0.85rem;
  }
  .alert.error {
    background: var(--danger-soft);
    border: 1px solid var(--danger);
    color: var(--text);
  }
  @media (max-width: 640px) {
    .field-row {
      grid-template-columns: 1fr;
    }
  }
</style>
